import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";

function useInView(ref, threshold) {
  var t = threshold === undefined ? 0.2 : threshold;
  var _a = useState(false), v = _a[0], setV = _a[1];
  useEffect(function() {
    if (!ref.current) return;
    var io = new IntersectionObserver(function(entries) {
      if (entries[0].isIntersecting) setV(true);
    }, { threshold: t });
    io.observe(ref.current);
    return function() { io.disconnect(); };
  }, [ref, t]);
  return v;
}

function Count({ to, suffix, dur, inView }) {
  var s = suffix === undefined ? "" : suffix;
  var d = dur === undefined ? 1600 : dur;
  var _a = useState(0), v = _a[0], setV = _a[1];
  useEffect(function() {
    if (!inView || typeof to !== "number") return;
    var raf;
    var start = performance.now();
    function tick(now) {
      var p = Math.min(1, (now - start) / d);
      setV(Math.floor((1 - Math.pow(1 - p, 3)) * to));
      if (p < 1) raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return function() { cancelAnimationFrame(raf); };
  }, [inView, to, d]);
  return React.createElement(React.Fragment, null, typeof to === "number" ? v + s : to);
}

function Lbl({ text, className, style }) {
  return (
    <span className={"inline-flex items-center gap-2 font-mono text-[0.6rem] tracking-mt uppercase " + (className || "")} style={{ color: "var(--mt-text-muted)", ...style }}>
      <span style={{ width: 8, height: 1, background: "var(--mt-text-muted)", display: "block" }} />
      {text}
    </span>
  );
}

function Tag({ children, style }) {
  return (
    <div className="absolute font-mono uppercase text-[0.52rem] tracking-mt opacity-25 pointer-events-none select-none" style={{ color: "var(--mt-text-muted)", ...style }} aria-hidden="true">
      {children}
    </div>
  );
}

function InfinityLogo({ size }) {
  var sz = size === undefined ? 320 : size;
  var canvasRef = useRef(null);
  var cameraRef = useRef(null);
  var mouseRef = useRef({ x: 0, y: 0 });

  useEffect(function() {
    var canvas = canvasRef.current;
    if (!canvas) return;
    var animFrame, renderer, scene, camera, model, levT = 0;

    function initScene() {
      scene = new THREE.Scene();
      camera = new THREE.PerspectiveCamera(45, canvas.clientWidth / canvas.clientHeight, 0.01, 100);
      camera.position.set(0, 0, 3.5);
      camera.lookAt(0, 0, 0);
      cameraRef.current = camera;

      renderer = new THREE.WebGLRenderer({ canvas: canvas, alpha: true, antialias: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setSize(canvas.clientWidth, canvas.clientHeight);
      renderer.outputEncoding = THREE.sRGBEncoding;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 2.4;

      // Iridescent chrome lighting
      scene.add(new THREE.AmbientLight(0xffffff, 0.9));
      var key = new THREE.DirectionalLight(0x88ccff, 4.5); key.position.set(2, 3, 4); scene.add(key);
      var fill = new THREE.DirectionalLight(0xC0A0FF, 3.0); fill.position.set(-3, 1, -2); scene.add(fill);
      var rim = new THREE.DirectionalLight(0xFFD7B5, 2.2); rim.position.set(0, -2, -3); scene.add(rim);
      var top = new THREE.DirectionalLight(0xffffff, 1.8); top.position.set(0, 5, 1); scene.add(top);

      var loader = new GLTFLoader();
      loader.load("/chrome_sigil_halo.glb", function(gltf) {
        model = gltf.scene;
        model.traverse(function(child) {
          if (child.isMesh) {
            var existing = child.material;
            child.material = new THREE.MeshPhysicalMaterial({
              color: new THREE.Color(0xc0d8ff),
              metalness: 1.0,
              roughness: 0.05,
              clearcoat: 1.0,
              clearcoatRoughness: 0.03,
              reflectivity: 1.0,
              envMapIntensity: 2.8,
              normalMap: existing && existing.normalMap ? existing.normalMap : null,
            });
          }
        });
        var box = new THREE.Box3().setFromObject(model);
        var center = box.getCenter(new THREE.Vector3());
        var sizeV = box.getSize(new THREE.Vector3());
        model.position.sub(center);
        model.scale.setScalar(2.2 / Math.max(sizeV.x, sizeV.y, sizeV.z));
        scene.add(model);
      }, undefined, function(err) { console.warn("GLB load err", err); });

      // Cursor tilt via model rotation
      function onMouse(e) {
        mouseRef.current.x = (e.clientX / window.innerWidth - 0.5) * 2;
        mouseRef.current.y = (e.clientY / window.innerHeight - 0.5) * 2;
      }
      window.addEventListener("mousemove", onMouse);

      (function animate() {
        animFrame = requestAnimationFrame(animate);
        levT += 0.008;
        if (model) {
          model.position.y = Math.sin(levT) * 0.06;
          // Slow-rotate base + cursor tilt
          model.rotation.y += 0.004;
          var tx = mouseRef.current.y * 0.45;
          var tz = mouseRef.current.x * -0.45;
          model.rotation.x += (tx - model.rotation.x) * 0.06;
          model.rotation.z += (tz - model.rotation.z) * 0.06;
        }
        renderer.render(scene, camera);
      })();

      return function() { window.removeEventListener("mousemove", onMouse); };
    }

    function loadScript(src, cb) {
      if (document.querySelector('script[src="' + src + '"]')) { cb(); return; }
      var s = document.createElement("script"); s.src = src; s.onload = cb; document.head.appendChild(s);
    }

    var cleanup;
    loadScript("https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js", function() {
      loadScript("https://cdn.jsdelivr.net/npm/three@0.128.0/examples/js/loaders/GLTFLoader.js", function() {
        cleanup = initScene(window.THREE, window.THREE.GLTFLoader);
      });
    });

    return function() { cancelAnimationFrame(animFrame); if (renderer) renderer.dispose(); if (cleanup) cleanup(); };
  }, []);

  return (
    <div style={{ width: sz * 2.4, height: sz, position: "relative", overflow: "visible" }} aria-hidden="true">
      <canvas ref={canvasRef} style={{ width: "100%", height: "100%", display: "block", position: "relative", zIndex: 1 }} width={Math.round(sz * 2.4)} height={sz} />
      {/* Levitation shadow */}
      <div style={{ position: "absolute", bottom: -(sz * 0.08), left: "18%", right: "18%", height: sz * 0.07, borderRadius: "50%", background: "radial-gradient(ellipse, rgba(0,100,200,0.22) 0%, rgba(192,160,255,0.12) 50%, transparent 75%)", filter: "blur(14px)", animation: "mt-shadow-pulse 4.5s ease-in-out infinite" }} />
    </div>
  );
}

function ChapterRail() {
  var _a = useState(0), pct = _a[0], setPct = _a[1];
  var _b = useState("00 · PROLOGUE"), ch = _b[0], setCh = _b[1];
  useEffect(function() {
    function fn() {
      var h = document.documentElement.scrollHeight - window.innerHeight;
      var p = Math.min(100, Math.max(0, (window.scrollY / h) * 100));
      setPct(p);
      if (p < 12) setCh("00 · PROLOGUE");
      else if (p < 25) setCh("01 · ORIGIN");
      else if (p < 40) setCh("02 · PROCESS");
      else if (p < 62) setCh("03 · PRINCIPLES");
      else if (p < 80) setCh("04 · PHILOSOPHY");
      else setCh("05 · EPILOGUE");
    }
    fn();
    window.addEventListener("scroll", fn, { passive: true });
    return function() { window.removeEventListener("scroll", fn); };
  }, []);
  return (
    <div className="fixed left-6 top-1/2 -translate-y-1/2 z-30 hidden lg:flex flex-col items-center gap-3" style={{ pointerEvents: "none" }}>
      <p className="font-mono text-[0.52rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)", writingMode: "vertical-rl", transform: "rotate(180deg)" }}>{ch}</p>
      <div className="relative w-px h-[240px]" style={{ background: "var(--mt-glass-border)" }}>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px" style={{ height: pct + "%", background: "linear-gradient(180deg,#00AAFF,#C0A0FF,#8B0000)", boxShadow: "0 0 10px rgba(0,170,255,0.5)", transition: "height 100ms linear" }} />
      </div>
      <p className="font-mono text-[0.52rem]" style={{ color: "var(--mt-text)" }}>{String(Math.round(pct)).padStart(3, "0")}</p>
    </div>
  );
}

function Prologue() {
  var ref = useRef(null);
  var iv = useInView(ref, 0.1);
  var _a = useState(false), done = _a[0], setDone = _a[1];
  useEffect(function() {
    var t = setTimeout(function() { setDone(true); }, 2000);
    return function() { clearTimeout(t); };
  }, []);
  return (
    <section ref={ref} className="relative flex items-start pt-2 pb-8 sm:pt-3 sm:pb-12 overflow-hidden page-first-section" style={{ isolation: "isolate" }}>
      <Tag style={{ top: "11%", left: "7%" }}>MT · ARCHIVE / 001</Tag>
      <Tag style={{ top: "18%", right: "9%" }}>DROP 01 · 2026</Tag>
      <Tag style={{ bottom: "14%", left: "11%" }}>3 DESIGNS · 5 VARIANTS</Tag>
      <Tag style={{ bottom: "20%", right: "8%" }}>MUMBAI · IN</Tag>
      <div aria-hidden="true" className="absolute select-none pointer-events-none font-display font-extralight" style={{ fontSize: "clamp(10rem,28vw,26rem)", color: "var(--mt-text)", opacity: 0.035, letterSpacing: "-0.04em", lineHeight: 1.0, left: "-2vw", bottom: "-6vh" }}>ABOUT</div>
      <div className="mt-container relative z-10 grid grid-cols-12 gap-6 items-center w-full py-8 sm:py-12">
        <div className="col-span-12 lg:col-span-10">
          <Lbl text="Chapter 00 · Prologue" className="mb-10" />
          <h1 className="font-display font-extralight" style={{ color: "var(--mt-text)", fontSize: "clamp(1.8rem,5.5vw,5.8rem)", letterSpacing: "-0.02em", lineHeight: 1.12, wordBreak: "keep-all" }}>
            {["A", "thousand"].map(function(w, i) {
              return <span key={i} className="inline-block" style={{ marginRight: "0.26em", opacity: iv ? 1 : 0, transform: iv ? "none" : "translateY(55%)", filter: iv ? "blur(0)" : "blur(5px)", transition: "all 900ms cubic-bezier(0.22,1,0.36,1) " + (200 + i * 60) + "ms" }}>{w}</span>;
            })}
            <br />
            {["knits,", "a", "few"].map(function(w, i) {
              return <span key={i} className="inline-block italic" style={{ marginRight: "0.26em", color: "var(--mt-text-muted)", opacity: iv ? 1 : 0, transform: iv ? "none" : "translateY(55%)", filter: iv ? "blur(0)" : "blur(5px)", transition: "all 900ms cubic-bezier(0.22,1,0.36,1) " + (380 + i * 60) + "ms" }}>{w}</span>;
            })}
            <br />
            {["hundred", "hands."].map(function(w, i) {
              return <span key={i} className={"inline-block italic mt-chrome-text mt-keep-anim"} style={{ marginRight: "0.26em", fontWeight: 700, opacity: iv ? 1 : 0, transform: iv ? "none" : "translateY(55%)", filter: iv ? "blur(0)" : "blur(5px)", transition: "all 900ms cubic-bezier(0.22,1,0.36,1) " + (560 + i * 60) + "ms" }}>{w}</span>;
            })}
          </h1>
          <span className="block mt-8" aria-hidden="true" style={{ height: 2, background: "#8B0000", boxShadow: "0 0 12px rgba(139,0,0,0.6)", width: 0, animation: "mt-line-grow 900ms cubic-bezier(0.16,1,0.3,1) 1400ms forwards", maxWidth: 200 }} />
          <p className={"mt-italic-silver mt-6 text-base sm:text-lg max-w-lg transition-opacity duration-1000 " + (done ? "opacity-100" : "opacity-0")}>And the millionth thread that binds it together.</p>
        </div>
      </div>
      <div className="absolute bottom-10 right-10 hidden sm:flex flex-col items-end gap-2" aria-hidden="true">
        <p className="font-mono text-[0.52rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)" }}>Begin descent</p>
        <span className="block w-px h-10" style={{ background: "linear-gradient(180deg, var(--mt-text-muted), transparent)", animation: "mt-bounce 2.4s ease-in-out infinite" }} />
      </div>
    </section>
  );
}

function Origin() {
  var wrapRef = useRef(null);
  var _a = useState(0), pct = _a[0], setPct = _a[1];
  useEffect(function() {
    function fn() {
      var el = wrapRef.current;
      if (!el) return;
      var r = el.getBoundingClientRect();
      setPct(Math.max(0, Math.min(1, -r.top / (r.height - window.innerHeight))));
    }
    fn();
    window.addEventListener("scroll", fn, { passive: true });
    return function() { window.removeEventListener("scroll", fn); };
  }, []);
  return (
    <section ref={wrapRef} className="relative" style={{ height: "min(115vh, 780px)" }} data-origin-scroll="true">
      <div className="sticky top-[68px] flex items-center overflow-hidden" style={{ height: "calc(100vh - 68px)" }}>
        <Lbl text="Chapter 01 · Origin" className="absolute top-12 left-6 sm:left-12" />
        <Tag style={{ top: "17%", right: "9%" }}>FILE / OR-001</Tag>
        <Tag style={{ bottom: "18%", left: "12%" }}>MEMORY 01 OF 04</Tag>
        <div aria-hidden="true" className="absolute inset-y-0 left-0 flex items-center pointer-events-none whitespace-nowrap" style={{ transform: "translate3d(" + (-pct * 28) + "vw,0,0)", willChange: "transform" }}>
          <span className="font-display font-medium" style={{ fontSize: "clamp(7rem,20vw,20rem)", color: "var(--mt-text)", opacity: 0.055, letterSpacing: "-0.05em" }}>ORIGIN&nbsp;·&nbsp;ORIGIN&nbsp;·&nbsp;ORIGIN</span>
        </div>

        <div className="mt-container relative z-10 grid grid-cols-12 gap-6 items-center" style={{ paddingTop: "clamp(3.5rem, 8vh, 5rem)" }}>
          <div className="col-span-12 md:col-span-7">
            <h2 className="font-display font-extralight" style={{ color: "var(--mt-text)", fontSize: "clamp(2.2rem,5.5vw,5rem)", lineHeight: 1.12, letterSpacing: "-0.02em" }}>
              <span className="block">2023.</span>
              <span className="block italic" style={{ color: "var(--mt-text-muted)", marginLeft: "0.7em" }}>An idea, tossed into the air.</span>
              <span className="block">It waited two years.</span>
              <span className="block italic mt-chrome-text mt-keep-anim" style={{ marginLeft: "0.35em", fontWeight: 700 }}>not abandoned, just early.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9 mt-6 md:mt-0">
            <div className="mt-glass mt-rainbow-edge rounded-[20px] p-6">
              <p className="font-mono text-[0.58rem] tracking-mt uppercase mb-3" style={{ color: "var(--mt-text-muted)" }}>Some things aren't built in a sprint</p>
              <p className="font-display font-light italic text-lg sm:text-xl" style={{ color: "var(--mt-text)", lineHeight: 1.4 }}>"Just kept, until they're ready to be kept by someone else too."</p>
              <div className="mt-5 flex items-center gap-2 text-[0.52rem] font-mono uppercase tracking-mt opacity-50" style={{ color: "var(--mt-text-muted)" }}>
                <span>March 2026</span><span>·</span><span>It stood on its own</span>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-0 right-0 px-6 sm:px-12 flex items-center justify-between">
          <p className="font-mono text-[0.52rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)" }}>FRAME {String(Math.round(pct * 100)).padStart(3, "0")} / 100</p>
          <div className="hidden sm:block w-32 h-px" style={{ background: "var(--mt-glass-border)" }}>
            <div className="h-full" style={{ width: (pct * 100) + "%", background: "var(--mt-text)" }} />
          </div>
        </div>
      </div>
    </section>
  );
}


function Process() {
  var ref = useRef(null);
  var iv = useInView(ref, 0.15);
  var r1 = useRef(null), r2 = useRef(null), r3 = useRef(null);
  var v1 = useInView(r1), v2 = useInView(r2), v3 = useInView(r3);
  var numStyle = function(color, v) {
    return { fontSize: "clamp(5rem,13vw,11rem)", color: color, letterSpacing: "-0.05em", lineHeight: 1.1, opacity: v ? 1 : 0, transform: v ? "translateY(0)" : "translateY(38px)", transition: "all 800ms cubic-bezier(0.22,1,0.36,1)" };
  };
  var steps = ["Research & sourcing", "Fabric testing", "Pattern making", "Sample review", "Final approval"];
  return (
    <section ref={ref} className="relative mt-section overflow-hidden">
      <Tag style={{ top: "8%", right: "9%" }}>FILE / PR-024</Tag>
      <Lbl text="Chapter 02 · Process" className="absolute top-10 left-6 sm:left-12" />
      <div className="mt-container relative z-10" style={{ paddingTop: "clamp(3.5rem, 7vh, 4.5rem)" }}>
        <div className="grid grid-cols-12 gap-6 mb-8 sm:mb-12">
          <div className="col-span-12 md:col-span-7">
            <h3 className="font-display font-extralight" style={{ color: "var(--mt-text)", fontSize: "clamp(2rem,4.5vw,4rem)", lineHeight: 1.12 }}>
              {[["Two years.", false], ["Forty-plus factories.", true], ["Twenty designs that didn't make it.", false]].map(function(pair, i) {
                return (
                  <span key={i} className={"inline " + (pair[1] ? "italic" : "")} style={{ display: "block", opacity: iv ? 1 : 0, transform: iv ? "none" : "translateY(55%)", filter: iv ? "blur(0)" : "blur(5px)", transition: "all 900ms ease " + (i * 140) + "ms", color: pair[1] ? "var(--mt-text-muted)" : "var(--mt-text)" }}>{pair[0]}</span>
                );
              })}
            </h3>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9 self-end">
            <p className="mt-italic-silver text-base leading-relaxed">Across India, over two years. Looking for people who hadn't forgotten what craftsmanship means.</p>
          </div>
        </div>
        <div className="grid grid-cols-12 gap-x-6 gap-y-0">
          <div ref={r1} className="col-span-12 md:col-span-5">
            <p className="font-display font-medium" style={numStyle("#00AAFF", v1)}><Count to={2} inView={v1} /></p>
            <p className="mt-3 font-mono text-[0.65rem] tracking-mt uppercase" style={{ color: "var(--mt-text)" }}>Years, Idea to Launch</p>
            <p className="mt-italic-silver mt-2 max-w-xs">Conceived in 2023. Properly started March 2026.</p>
            <div className="mt-6 space-y-2 max-w-xs">
              {steps.map(function(step, i) {
                return (
                  <div key={step} className="flex items-center gap-3">
                    <div className="h-px flex-1 rounded-full overflow-hidden" style={{ background: "var(--mt-glass-border)" }}>
                      <div className="h-full rounded-full" style={{ width: v1 ? ((i + 1) * 20) + "%" : "0%", background: "linear-gradient(90deg,#00AAFF,rgba(0,170,255,0.3))", transition: "width 600ms ease " + (200 + i * 100) + "ms" }} />
                    </div>
                    <span className="font-mono text-[0.52rem] tracking-mt uppercase whitespace-nowrap" style={{ color: "var(--mt-text-muted)" }}>{step}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <div ref={r2} className="col-span-12 md:col-span-4 md:col-start-7 md:mt-12 lg:mt-24">
            <p className="font-display font-medium" style={numStyle("#C0A0FF", v2)}><Count to={40} suffix="+" inView={v2} /></p>
            <p className="mt-3 font-mono text-[0.65rem] tracking-mt uppercase" style={{ color: "var(--mt-text)" }}>Factories Visited</p>
            <p className="mt-italic-silver mt-2 max-w-xs">Across India, before one was approved to carry the name.</p>
          </div>
          <div ref={r3} className="col-span-12 md:col-span-6 md:col-start-3 md:mt-10 lg:mt-20">
            <p className="font-display font-medium" style={{ ...numStyle("#8B0000", v3), textShadow: "0 6px 40px rgba(139,0,0,0.4)" }}><Count to={20} inView={v3} /></p>
            <p className="mt-3 font-mono text-[0.65rem] tracking-mt uppercase" style={{ color: "var(--mt-text)" }}>Designs Discarded</p>
            <p className="mt-italic-silver mt-2 max-w-xs">Before three were approved for Drop 01.</p>
            <div className="mt-6 max-w-xs">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-[0.55rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)" }}>Drop 01</span>
                <span className="font-mono text-[0.55rem] tracking-mt" style={{ color: "#8B0000", fontWeight: 700 }}>3 DESIGNS · 2 IN TWO COLOURWAYS</span>
              </div>
              <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--mt-glass-border)" }}>
                <div className="h-full rounded-full" style={{ width: v3 ? "100%" : "0%", background: "linear-gradient(90deg,#8B0000,rgba(139,0,0,0.4))", transition: "width 1200ms cubic-bezier(0.22,1,0.36,1) 400ms" }} />
              </div>
              <p className="mt-2 font-mono text-[0.52rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)" }}>Combed cotton · biowashed</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

var PANELS = [
  { word: "PREMIUM",   code: "PR-01", body: "Combed cotton. Double biowashed, silicon softened. Nothing synthetic for the sake of price.", aside: "Weight matters. Hand matters. The fabric speaks before the cut does.", tone: "blue" },
  { word: "LIMITED",   code: "PR-02", body: "Small runs. Never restocked. The drop exists once, then lives on the wearer.", aside: "Scarcity isn't strategy. It's a consequence of intention.", tone: "violet" },
  { word: "INTENTION", code: "PR-03", body: "Every hem, every seam, every label is a decision.", aside: "Default is the enemy of design. Each choice earns its place.", tone: "chrome" },
  { word: "FUTURE",    code: "PR-04", body: "We don't follow trends. We build for what's coming.", aside: "The horizon is closer than people think. We're already there.", tone: "blood" },
];

function PrinciplePanel({ p, idx }) {
  var ref = useRef(null);
  var _a = useState(false), vis = _a[0], setVis = _a[1];
  useEffect(function() {
    var io = new IntersectionObserver(function(entries) {
      if (entries[0].isIntersecting) setVis(true);
    }, { threshold: 0.15 });
    if (ref.current) io.observe(ref.current);
    return function() { io.disconnect(); };
  }, []);
  var boxOnRight = idx % 2 === 0;
  var glow = { blue: "rgba(0,170,255,0.09)", violet: "rgba(192,160,255,0.09)", chrome: "rgba(245,245,245,0.04)", blood: "rgba(139,0,0,0.1)" };
  return (
    <section ref={ref} className="relative overflow-hidden principle-section" style={{ isolation: "isolate" }}>
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(55% 65% at 50% 50%, " + glow[p.tone] + ", transparent 70%)" }} />
      <Tag style={{ top: "10%", left: "6%" }}>{p.code}</Tag>
      <Tag style={{ top: "10%", right: "6%" }}>0{idx + 1} / 04</Tag>

      {/* Desktop (lg+): watermark word full-width + translucent overlay box */}
      <div className="absolute inset-0 items-center overflow-hidden pointer-events-none hidden lg:flex">
        <h2 className="font-display font-medium select-none whitespace-nowrap w-full"
          style={{
            fontSize: "clamp(7rem,17vw,16rem)",
            color: "var(--mt-panel-" + p.tone + ")",
            letterSpacing: "-0.045em", lineHeight: 1.0,
            textShadow: p.tone === "blood" ? "0 4px 50px rgba(139,0,0,0.45)" : "none",
            textAlign: boxOnRight ? "left" : "right",
            paddingLeft: boxOnRight ? "4vw" : "0",
            paddingRight: boxOnRight ? "0" : "4vw",
            opacity: vis ? 1 : 0, filter: vis ? "blur(0)" : "blur(8px)",
            transform: vis ? "translateX(0)" : (boxOnRight ? "translateX(-60px)" : "translateX(60px)"),
            transition: "all 1000ms cubic-bezier(0.22,1,0.36,1)",
          }}>{p.word}</h2>
      </div>
      <div className="hidden lg:block absolute z-30"
        style={{
          right: boxOnRight ? "5%" : "auto",
          left: boxOnRight ? "auto" : "5%",
          top: "50%", transform: "translateY(-50%)",
          width: "clamp(300px, 32vw, 400px)",
          opacity: vis ? 1 : 0,
          transition: "all 900ms cubic-bezier(0.22,1,0.36,1) 200ms",
        }}>
        <PrincipleBox p={p} />
      </div>

      {/* Mobile + Tablet (< lg): stacked — word full-width, box below */}
      <div className="lg:hidden mt-container relative z-10">
        <div className="font-display font-medium select-none mb-5 w-full"
          style={{
            fontSize: "clamp(3.5rem,13vw,9rem)",
            color: "var(--mt-panel-" + p.tone + ")",
            letterSpacing: "-0.03em", lineHeight: 1.0,
            opacity: vis ? 1 : 0,
            transition: "all 800ms cubic-bezier(0.22,1,0.36,1)",
          }}>{p.word}</div>
        <div style={{ opacity: vis ? 1 : 0, transition: "all 800ms cubic-bezier(0.22,1,0.36,1) 150ms" }}>
          <PrincipleBox p={p} />
        </div>
      </div>
    </section>
  );
}

function PrincipleBox({ p }) {
  return (
    <div className="mt-rainbow-edge rounded-[18px] p-5" style={{ background: "rgba(14,14,18,0.7)", backdropFilter: "blur(48px) saturate(200%)", WebkitBackdropFilter: "blur(48px) saturate(200%)", border: "1px solid rgba(255,255,255,0.16)", boxShadow: "0 24px 60px -16px rgba(0,0,0,0.75), inset 0 1px 0 rgba(255,255,255,0.08)" }}>
      <p className="font-mono text-[0.52rem] tracking-mt uppercase mb-3" style={{ color: "rgba(255,255,255,0.45)" }}>Principle / {p.code}</p>
      <p className="font-display font-light text-lg" style={{ color: "#F5F5F5", lineHeight: 1.4 }}>{p.body}</p>
      <div className="mt-4 pt-4 border-t" style={{ borderColor: "rgba(255,255,255,0.09)" }}>
        <p className="font-mono text-[0.52rem] tracking-mt uppercase mb-1.5" style={{ color: "rgba(255,255,255,0.35)" }}>Footnote</p>
        <p className="font-body italic text-[0.82rem]" style={{ color: "rgba(255,255,255,0.8)", lineHeight: 1.6 }}>{p.aside}</p>
      </div>
    </div>
  );
}

function Principles() {
  return (
    <div>
      <section className="mt-section" style={{ paddingBottom: "clamp(1rem,3vw,2.5rem)" }}>
        <div className="mt-container">
          <Lbl text="Chapter 03 · Principles" className="mb-6" />
          <h3 className="font-display font-extralight" style={{ color: "var(--mt-text)", fontSize: "clamp(2rem,4.5vw,3.8rem)", lineHeight: 1.12 }}>
            Four rules. <span className="italic mt-chrome-text mt-keep-anim" style={{ fontWeight: 600 }}>No exceptions.</span>
          </h3>
        </div>
      </section>
      {PANELS.map(function(p, i) { return <PrinciplePanel key={p.word} p={p} idx={i} />; })}
    </div>
  );
}

function Philosophy() {
  var ref = useRef(null);
  var iv = useInView(ref, 0.2);
  return (
    <section ref={ref} className="relative flex items-center mt-section">
      <Lbl text="Chapter 04 · Philosophy" className="absolute top-10 left-6 sm:left-12" />
      <Tag style={{ top: "11%", right: "7%" }}>FILE / PH-01</Tag>
      <Tag style={{ bottom: "18%", right: "9%" }}>VOLUME · 01</Tag>
      {/* Glow bloom — unconstrained, behind everything */}
      <div className="absolute hidden lg:block" style={{ right: "8%", top: "50%", transform: "translateY(-50%)", width: "70vw", height: "70vh", pointerEvents: "none", zIndex: 0 }}>
        <div style={{ position: "absolute", top: "50%", left: "50%", width: "100%", height: "100%", transform: "translate(-50%,-50%)", borderRadius: "50%", background: "radial-gradient(ellipse, rgba(0,170,255,0.30) 0%, rgba(192,160,255,0.24) 35%, rgba(255,180,100,0.12) 60%, transparent 75%)", filter: "blur(60px)", animation: "mt-glow-pulse 7s ease-in-out infinite" }} />
      </div>
      {/* GLB halo — directly on top of glow, nothing between */}
      <div className="absolute hidden lg:block" style={{ right: "2%", top: "50%", transform: "translateY(-50%)", width: 520, height: 260, zIndex: 1 }}>
        <InfinityLogo size={340} />
      </div>
      <div className="mt-container relative z-10" style={{ maxWidth: "56rem", paddingTop: "clamp(3.5rem, 8vh, 5rem)" }}>
        <p className="font-mono text-[0.58rem] tracking-mt uppercase mb-8" style={{ color: "var(--mt-text-muted)" }}>On Culture &amp; Craft</p>
        <p className="font-display font-extralight italic" style={{ color: "var(--mt-text)", fontSize: "clamp(1.7rem,5vw,4.5rem)", lineHeight: 1.12, opacity: iv ? 1 : 0, transform: iv ? "translateY(0)" : "translateY(28px)", transition: "all 1100ms cubic-bezier(0.22,1,0.36,1)" }}>
          "Passion and <span className="not-italic" style={{ fontWeight: 700, color: "#B81D1D", WebkitTextFillColor: "#B81D1D" }}>taste</span> can coexist. We believe appreciation for <span className="not-italic" style={{ color: "var(--mt-text-muted)" }}>art and storytelling</span> deserves the same craftsmanship as any <span className="not-italic" style={{ color: "#8B0000" }}>luxury garment.</span>"
        </p>
        <div className="mt-10 flex items-center gap-4">
          <span className="block w-16 h-px" style={{ background: "var(--mt-text-muted)" }} />
          <p className="font-mono text-[0.58rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)" }}>Million Threads · Founding Principle</p>
        </div>
      </div>
    </section>
  );
}

function Epilogue() {
  var ref = useRef(null);
  var iv = useInView(ref, 0.2);
  return (
    <section ref={ref} className="relative flex items-center justify-center mt-section" style={{ isolation: "isolate" }}>
      <Tag style={{ bottom: "14%", left: "9%" }}>END · ARCHIVE / 001</Tag>
      <Tag style={{ bottom: "14%", right: "9%" }}>BEGIN · ARCHIVE / 002</Tag>
      <div className="mt-container relative z-10 text-center">
        <p className="font-mono text-[0.58rem] tracking-mt uppercase mb-7" style={{ color: "var(--mt-text-muted)" }}>End of file · Beginning of yours</p>
        <div className="flex justify-center mb-8">
          <img src="/logo.png" alt="Million Threads" style={{ width: 72, height: 72, objectFit: "contain", opacity: 0.7, filter: "invert(var(--mt-logo-invert)) drop-shadow(0 0 16px rgba(0,170,255,0.3))" }} />
        </div>
        <h2 className="font-display font-extralight" style={{ color: "var(--mt-text)", fontSize: "clamp(3rem,11vw,9.5rem)", lineHeight: 1.1 }}>
          <span className="block" style={{ opacity: iv ? 1 : 0, transform: iv ? "translateY(0)" : "translateY(36px)", transition: "all 1000ms cubic-bezier(0.22,1,0.36,1)", filter: iv ? "blur(0)" : "blur(7px)" }}>Your turn,</span>
          <span className="block italic mt-chrome-text mt-keep-anim" style={{ fontWeight: 700, opacity: iv ? 1 : 0, transform: iv ? "translateY(0)" : "translateY(36px)", transition: "all 1000ms cubic-bezier(0.22,1,0.36,1) 200ms", filter: iv ? "blur(0)" : "blur(7px)" }}>to write.</span>
        </h2>
        <p className="mt-8 mt-italic-silver text-base sm:text-lg" style={{ opacity: iv ? 1 : 0, transition: "opacity 800ms ease 600ms" }}>A million bows. A million threads of love. One promise: greatness.</p>
        <p className="mt-3 font-mono text-[0.6rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)", opacity: iv ? 1 : 0, transition: "opacity 800ms ease 800ms" }}>Yours faithfully, MILLION THREADS.</p>
        <div className="mt-12 flex flex-wrap gap-4 justify-center">
          <Link to="/shop" className="mt-btn mt-rainbow-edge inline-flex items-center gap-2">Enter the Shop <ArrowRight size={14} /></Link>
          <Link to="/contact" className="mt-btn inline-flex items-center gap-2">Open Channel <ArrowUpRight size={14} /></Link>
        </div>
      </div>
    </section>
  );
}

export default function About() {
  return (
    <>
      <ChapterRail />
      <Prologue />
      <Origin />
      <Process />
      <Principles />
      <Philosophy />
      <Epilogue />
    </>
  );
}
